import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";

/**
 * FadeUp — slide up + fade in when in view (once).
 */
export const FadeUp = ({ children, delay = 0, y = 24, duration = 0.7, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration, delay, ease: [0.22, 1, 0.36, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

/**
 * Parse a numeric value from strings like "10億+", "3000萬", "300+", "41,000".
 * Returns { value: number, suffix: string, prefix: string } used to animate the number portion.
 */
function parseStat(raw) {
  const str = String(raw).trim();
  // Split into tokens: digits and non-digits
  const match = str.match(/^([^\d]*)([\d,\.]+)(.*)$/);
  if (!match) return { prefix: "", value: 0, suffix: str };
  const prefix = match[1] || "";
  const digits = match[2].replace(/,/g, "");
  const suffix = match[3] || "";
  const value = parseFloat(digits);
  const hasComma = match[2].includes(",");
  return { prefix, value: isNaN(value) ? 0 : value, suffix, hasComma };
}

function format(n, hasComma) {
  const rounded = Math.round(n);
  return hasComma ? rounded.toLocaleString() : String(rounded);
}

export const CountUp = ({ value, duration = 1.2, className = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "0px 0px -10% 0px" });
  const { prefix, value: target, suffix, hasComma } = parseStat(value);
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!inView) return;
    let start;
    let raf;
    const ease = (t) => 1 - Math.pow(1 - t, 3); // easeOutCubic
    const step = (ts) => {
      if (!start) start = ts;
      const p = Math.min(1, (ts - start) / (duration * 1000));
      setDisplay(target * ease(p));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => raf && cancelAnimationFrame(raf);
  }, [inView, target, duration]);

  return (
    <span ref={ref} className={className}>
      {prefix}
      {format(display, hasComma)}
      {suffix}
    </span>
  );
};

/**
 * Typewriter effect revealing characters one by one.
 * Respects prefers-reduced-motion (shows full text immediately).
 */
export const Typewriter = ({
  text,
  speed = 80,
  startDelay = 400,
  cursor = true,
  className = "",
}) => {
  const [out, setOut] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) {
      setOut(text);
      setDone(true);
      return;
    }
    let i = 0;
    let timeoutId;
    const tick = () => {
      if (i <= text.length) {
        setOut(text.slice(0, i));
        i += 1;
        timeoutId = setTimeout(tick, speed);
      } else {
        setDone(true);
      }
    };
    const start = setTimeout(tick, startDelay);
    return () => {
      clearTimeout(start);
      clearTimeout(timeoutId);
    };
  }, [text, speed, startDelay]);

  return (
    <span className={className}>
      {out}
      {cursor && (
        <motion.span
          aria-hidden
          className="inline-block w-[2px] sm:w-[3px] h-[0.85em] align-[-0.08em] ml-1 bg-current"
          animate={{ opacity: done ? [1, 0, 1] : 1 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
        />
      )}
    </span>
  );
};
